import React from 'react';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { isMobile } from 'react-device-detect';
import { useNavigate } from 'react-router-dom';
import { formatTime, useIsMobileDimensions } from '../../shared/utils';
import { PuzzleNamesForUI } from '../../../../utils/types';
import { PuzzleNames } from '../../../../utils/types';
import StatsDialog from '../../shared/StatsDialog';
import RevealConfirmationDialog from '../../shared/RevealConfirmationDialog/RevealConfirmationDialog';
import { Store } from 'pullstate';
import { PuzzleStoreBase } from '../../../../utils/types';
import TriwordLifelineButton from '../../Triword/components/LifelineButton';
import PathwordLifelineButton from '../../Pathword/components/LifelineButton';
import './PuzzleTopBar.less';

type PuzzleTopBarProps = {
  puzzleName: PuzzleNames;
  store: Store<PuzzleStoreBase>;
  onSave: () => void;
  onReveal: () => void;
  InstructionsComponent: React.ComponentType;
};

const PuzzleTopBar = ({
  puzzleName,
  store,
  onSave,
  onReveal,
  InstructionsComponent,
}: PuzzleTopBarProps) => {
  const { solved, revealed, time, isPaused } = store.useState();

  const useMobileStyles = isMobile || useIsMobileDimensions();
  const navigate = useNavigate();

  const pauseButtonClass = (solved || revealed) ? 'hidden' : '';

  return (
    <div className={`puzzleTopBar ${useMobileStyles ? 'mobile' : ''}`}>
      <div className="topBarLeftSection">
        <IconButton disableRipple title="Back" onClick={() => {
          navigate('/');
          window.scrollTo(0, 0);
          onSave();
        }}>
          <KeyboardBackspaceIcon/>
        </IconButton>
        <div className="navigationLabel">{PuzzleNamesForUI[puzzleName]}</div>
      </div>

      <div className="topBarMiddleSection">
        <div className="timer">{formatTime(time)}</div>
        <IconButton className={pauseButtonClass} title="Pause" onClick={() => {
          store.update(s => {
            if (!s.isPaused) {
              onSave();
            }
            s.isPaused = !s.isPaused;
          });
          window.scrollTo(0, 0);
        }}>
          {isPaused ? <PlayArrowIcon/> : <PauseIcon/>}
        </IconButton>
      </div>

      <div className="topBarRightSection">
        <InstructionsComponent/>
        <RevealConfirmationDialog store={store} onReveal={onReveal}/>
        <StatsDialog puzzleName={puzzleName} store={store} />

        <IconButton disableRipple title="How to Play" onClick={() => {
          store.update(s => {
            s.instructionsAreOpen = true;
            s.isSoftPaused = true;
          });
        }}>
          <HelpOutlineIcon/>
        </IconButton>

        {puzzleName === PuzzleNames.Triword && <TriwordLifelineButton/>}
        {(puzzleName === PuzzleNames.Pathword || puzzleName === PuzzleNames.Pathword5x5) &&
          <PathwordLifelineButton store={store as any} onSave={onSave}/>}

        <IconButton disableRipple disabled={revealed || solved} title="Reveal" onClick={() => {
          store.update(s => {
            s.revealConfirmationIsOpen = true;
            s.isSoftPaused = true;
          });
        }}>
          <VisibilityIcon/>
        </IconButton>

        <IconButton disableRipple title="Stats" onClick={() => {
          store.update(s => {
            s.statsDialogIsOpen = true;
            s.isSoftPaused = true;
          });
        }}>
          <EqualizerIcon/>
        </IconButton>
      </div>
    </div>
  );
};

export default PuzzleTopBar;